import React, {useEffect, useState} from "react";
import '../assets/css/Dentobook.css';
import DetailWrapper from "../components/common/DetailWrapper";
import {Button, Col, Form, Input, message, Modal, Row, Spin, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {Link, useNavigate} from "react-router-dom";
import {isMobileOnly} from "react-device-detect";
import {CloseOutlined, ExclamationCircleOutlined, PlusOutlined, SearchOutlined} from "@ant-design/icons";
import {
    Organization
} from "../api-client";
import {accessibilityRoles} from "../utils/accessUtils";
import AccessControl from "../components/common/AccessControl";
import {getAccountStatus} from "../i18n/utils";
import {getCountry} from "../i18n/countries";
import {getOrganizationApi, getUserApi, getPatientApi, getCalendarApi} from "../api/apiInstances";
import logger from "../utils/logger";

const Organizations = () => {

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [keyword, setKeyword] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [orgToDelete, setOrgToDelete] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const organizationApi = getOrganizationApi();
    const userApi = getUserApi();

    const navigate = useNavigate();
    const intl = useIntl();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        setIsLoading(true);
        organizationApi.fetchAllOrganizations(keyword)
            .then(res => {
                const orgs = res.data;

                if (orgs.length === 0) {
                    setOrganizations([]);
                    setIsLoading(false);
                    return;
                }

                // Get all organization IDs as array
                const orgIds = orgs.map(org => org.id);

                // Get API instances
                const patientApi = getPatientApi();
                const calendarApi = getCalendarApi();
                const userApi = getUserApi();

                // Fetch all counts in 3 batch-get requests using generated API clients
                return Promise.all([
                    patientApi.batchGetPatientsCounts({
                        organization_ids: orgIds
                    })
                        .then((res) => res.data)
                        .catch((err: any) => {
                            logger.error('Failed to fetch patient counts:', err);
                            return {} as Record<string, number>;
                        }),
                    calendarApi.batchGetCalendarEventsCounts({
                        organization_ids: orgIds
                    })
                        .then((res) => res.data)
                        .catch((err: any) => {
                            logger.error('Failed to fetch calendar counts:', err);
                            return {} as Record<string, number>;
                        }),
                    userApi.batchGetLoginCounts({
                        organization_ids: orgIds
                    })
                        .then((res) => res.data)
                        .catch((err: any) => {
                            logger.error('Failed to fetch login counts:', err);
                            return {} as Record<string, number>;
                        })
                ]).then(([patientCounts, calendarCounts, loginCounts]) => {
                    // Merge counts into organizations
                    const updatedOrgs = orgs.map(org => {
                        const orgId = org.id as string;
                        return {
                            ...org,
                            patientCount: patientCounts[orgId] ?? 0,
                            calendarEventCount: calendarCounts[orgId] ?? 0,
                            loginCount: loginCounts[orgId] ?? 0
                        };
                    });
                    setOrganizations(updatedOrgs);
                });
            })
            .catch(err => {
                logger.error('Failed to load organizations:', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const showDeleteModal = (organization: Organization) => {
        setOrgToDelete(organization);
        setIsModalOpen(true);
    };

    const handleOk = () => {
        if (orgToDelete?.id) {
            organizationApi.deleteOrganization(orgToDelete.id)
                .then(() => {
                    return userApi.deleteUsersByOrganization(orgToDelete.id as string);
                })
                .then(() => {
                    message.success(
                        intl.formatMessage({
                            id: "delete_success",
                            defaultMessage: "Clinic deleted successfully."
                        })
                    );
                    loadData();
                })
                .catch((err: any) => {
                    message.error(
                        intl.formatMessage({
                            id: "delete_error",
                            defaultMessage: "Failed to delete clinic."
                        })
                    );
                })
                .finally(() => {
                    setIsModalOpen(false);
                    setOrgToDelete(null);
                });
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setOrgToDelete(null);
    };

    const modalFooter = (
        <>
            <Button type="primary" onClick={handleOk}>
                <FormattedMessage id="delete" defaultMessage="Delete" />
            </Button>
            <Button onClick={handleCancel} style={{ marginLeft: "8px" }}>
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
        </>
    );

    return (
        <AccessControl accessibleFor={[accessibilityRoles.PLATFORM_ADMINISTRATOR]}>
            <DetailWrapper navigateBack={undefined}>
                <Row className="mb-2">
                    <Col span={12}>
                        <h3><FormattedMessage id="organizations"/></h3>
                    </Col>
                    <Col span={12}>
                        <Link style={{float: 'right', marginTop: '25px'}} to={"/organizations/create"}>
                            <div className={isMobileOnly ? "mobile-btn-wrapper" : ""}>
                                <Button shape="round"
                                        className={isMobileOnly ? "mobile-add-btn" : "dentobook-btn-primary dentobook-btn-medium"}
                                        type="primary"><PlusOutlined/> <FormattedMessage id="add"/></Button>
                            </div>
                        </Link>
                    </Col>
                </Row>
                <Form layout="horizontal">
                    <Form.Item
                        name="keyword"
                        className={"dentobook-form-item"}
                    >
                        <Input value={keyword}
                               placeholder={useIntl().formatMessage({id: 'search'})}
                               onChange={e => setKeyword(e.target.value)}
                               prefix={<SearchOutlined style={{fontSize: 24}}/>}
                        />
                    </Form.Item>
                    <Button type="link" htmlType="submit" onClick={loadData}></Button>
                </Form>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin tip="Loading..." />
                    </div>
                ) : (
                <Table dataSource={organizations}
                       pagination={false}
                       className="dentobook-table"
                       onRow={(organization: Organization) => {
                           return {
                               onClick: e => {
                                   navigate('/organizations/' + organization.id);
                               }
                           };
                       }}>
                    <Table.Column
                        key="name"
                        render={(organization) => organization.name}
                        title={<FormattedMessage id="name"/>}
                        sorter={(a: any, b: any) => (a.name || '').localeCompare(b.name || '')}
                    />
                    <Table.Column
                        key="country"
                        render={(organization) => {
                            const countryData = getCountry(organization.country);
                            return countryData ? (intl.locale === 'BG' ? countryData.name_bg : countryData.name_en) : (organization.country || '');
                        }}
                        title={<FormattedMessage id="country"/>}
                        sorter={(a: any, b: any) => (a.country || '').localeCompare(b.country || '')}
                    />
                    <Table.Column
                        key="id"
                        render={(organization) => organization.id}
                        title={<FormattedMessage id="ID"/>}
                        sorter={(a: any, b: any) => (a.id || '').localeCompare(b.id || '')}
                    />
                    <Table.Column
                        key="clinicStatus"
                        render={(organization) => getAccountStatus(organization, intl)}
                        title={<FormattedMessage id="account_status"/>}
                        sorter={(a: any, b: any) => {
                            const statusA = getAccountStatus(a, intl) || '';
                            const statusB = getAccountStatus(b, intl) || '';
                            return statusA.localeCompare(statusB);
                        }}
                    />
                    <Table.Column
                        key="patientCount"
                        render={(organization: any) => organization.patientCount}
                        title={<FormattedMessage id="patients_count" defaultMessage="Patients" />}
                        sorter={(a: any, b: any) => (a.patientCount || 0) - (b.patientCount || 0)}
                        defaultSortOrder="descend"
                    />
                    <Table.Column
                        key="calendarEventCount"
                        render={(organization: any) => organization.calendarEventCount}
                        title={<FormattedMessage id="calendar_appointments_count" defaultMessage="Appointments" />}
                        sorter={(a: any, b: any) => (a.calendarEventCount || 0) - (b.calendarEventCount || 0)}
                    />
                    <Table.Column
                        key="createdAt"
                        render={(organization: Organization) => {
                            const date = organization.createdAt ? new Date(organization.createdAt) : null;
                            return date ? date.toLocaleString() : '';
                        }}
                        title={<FormattedMessage id="createdAt" defaultMessage="Created on" />}
                        sorter={(a, b) => {
                            const orgA = a as Organization;
                            const orgB = b as Organization;
                            const timeA = orgA.createdAt ? new Date(orgA.createdAt).getTime() : 0;
                            const timeB = orgB.createdAt ? new Date(orgB.createdAt).getTime() : 0;
                            return timeA - timeB;
                        }}
                    />
                    <Table.Column
                        key="loginCount"
                        title={<FormattedMessage id="login_count_label" />}
                        render={(organization: any) => organization.loginCount}
                        sorter={(a: any, b: any) => (a.loginCount || 0) - (b.loginCount || 0)}
                    />
                    <Table.Column
                        key="actions"
                        title={<FormattedMessage id="actions" defaultMessage="Actions" />}
                        render={(organization: Organization) => (
                            <Button
                                type="primary"
                                danger
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showDeleteModal(organization);
                                }}
                            >
                                <FormattedMessage id="delete" defaultMessage="Delete" />
                            </Button>
                        )}
                    />
                </Table>
                )}
                <Modal
                    transitionName=""
                    footer={modalFooter}
                    className="dentobook-modal"
                    title={
                        <span style={{ color: 'red' }}>
          <ExclamationCircleOutlined style={{ marginRight: 8 }} />
          <FormattedMessage id="delete_organization" />
        </span>
                    }
                    closeIcon={<CloseOutlined className="icon-24px" onClick={handleCancel} />}
                    open={isModalOpen}
                    onOk={handleOk}
                    onCancel={handleCancel}
                >
                    <div style={{ textAlign: 'center' }}>
                        <ExclamationCircleOutlined style={{ fontSize: '48px', color: 'red', marginBottom: '16px' }} />
                        <h3 className={"dentobook-content font-weight-500 font-24 text-align-center " + (isMobileOnly ? "mt-112" : "")}>
                            <FormattedMessage id="delete_organization_confirmation_text" />
                        </h3>
                    </div>
                </Modal>
            </DetailWrapper>
        </AccessControl>
    )
};

export default Organizations;